/**
 * Disposition Config Panel - Drag-and-drop configurable list
 * Supports Dispositions, Timeframes, and Suppression Reasons
 */
import React, { useState, useCallback } from 'react';
import {
  List,
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
  InputNumber,
  Space,
  Typography,
  Tag,
  Popconfirm,
  Row,
  Col,
  Alert
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Sortable Table Row Component
function SortableRow({ id, item, type, onEdit, onDelete, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#e6f7ff' : 'white',
    cursor: 'grab',
    userSelect: 'none'
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
    </tr>
  );
}

function DispositionConfigPanel({
  type,
  data = [],
  inProgressData = [],
  closedData = [],
  onCreate,
  onUpdate,
  onDelete,
  onReorder
}) {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  // For decisions: maintain separate lists for in_progress and closed
  const [inProgressItems, setInProgressItems] = useState(inProgressData);
  const [closedItems, setClosedItems] = useState(closedData);

  // For timeframes and suppression: single list
  const [items, setItems] = useState(data);

  // Update local state when props change
  React.useEffect(() => {
    if (type === 'dispositions') {
      setInProgressItems(inProgressData);
      setClosedItems(closedData);
    } else {
      setItems(data);
    }
  }, [type, data, inProgressData, closedData]);

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event, status = null) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    if (type === 'dispositions') {
      const sourceItems = status === 'in_progress' ? inProgressItems : closedItems;
      const setSourceItems = status === 'in_progress' ? setInProgressItems : setClosedItems;

      const oldIndex = sourceItems.findIndex(item => item.id === active.id);
      const newIndex = sourceItems.findIndex(item => item.id === over.id);

      if (oldIndex !== newIndex) {
        const newItems = arrayMove(sourceItems, oldIndex, newIndex);
        setSourceItems(newItems);

        // Call API to reorder
        const orderedIds = newItems.map(item => item.id);
        await onReorder(status, orderedIds);
      }
    } else {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);

      if (oldIndex !== newIndex) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);

        // Call API to reorder
        const orderedIds = newItems.map(item => item.id);
        await onReorder(orderedIds);
      }
    }
  };

  const showAddModal = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const showEditModal = (item) => {
    setEditingItem(item);
    form.setFieldsValue(item);
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      let success;
      if (editingItem) {
        success = await onUpdate(editingItem.id, values);
      } else {
        success = await onCreate(values);
      }

      if (success !== false) {
        setModalVisible(false);
        form.resetFields();
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  const handleDelete = async (id) => {
    await onDelete(id);
  };

  const renderModalFields = () => {
    if (type === 'dispositions') {
      return (
        <>
          <Form.Item
            name="disposition_name"
            label="Decision Name"
            rules={[{ required: true, message: 'Please enter decision name' }]}
          >
            <Input placeholder="e.g., Fixed, False Positive" maxLength={50} />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status Type"
            rules={[{ required: true, message: 'Please select status type' }]}
          >
            <Select placeholder="Select status">
              <Option value="in_progress">In Progress</Option>
              <Option value="closed">Closed</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="disposition_type"
            label="Applies To"
            rules={[{ required: true, message: 'Please select decision type' }]}
          >
            <Select placeholder="Select type">
              <Option value="failure">Failures Only</Option>
              <Option value="warning">Warnings Only</Option>
              <Option value="both">Both Failures & Warnings</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="requires_comment"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Requires Comment</Checkbox>
          </Form.Item>

          <Form.Item
            name="is_time_bound"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Time-Bound (requires Wait Until selection)</Checkbox>
          </Form.Item>
        </>
      );
    } else if (type === 'timeframes') {
      return (
        <>
          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: 'Please enter label' }]}
          >
            <Input placeholder="e.g., 3 months, Indefinitely" maxLength={50} />
          </Form.Item>

          <Form.Item
            name="duration_days"
            label="Duration (Days)"
            extra="Leave empty for 'Indefinitely'"
          >
            <InputNumber
              min={1}
              max={3650}
              placeholder="e.g., 90 for 3 months"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </>
      );
    } else if (type === 'suppression') {
      return (
        <Form.Item
          name="reason_name"
          label="Reason Name"
          rules={[{ required: true, message: 'Please enter reason name' }]}
        >
          <Input placeholder="e.g., Business Exception, Technical Limitation" maxLength={100} />
        </Form.Item>
      );
    }
  };

  const getTitle = () => {
    if (type === 'dispositions') return 'Dispositions';
    if (type === 'timeframes') return 'Wait Until Timeframes';
    if (type === 'suppression') return 'Suppression Reasons';
  };

  const getModalTitle = () => {
    const action = editingItem ? 'Edit' : 'Add';
    return `${action} ${getTitle().slice(0, -1)}`;
  };

  const getAppliesTo = (dispositionType) => {
    if (dispositionType === 'failure') return 'Failures';
    if (dispositionType === 'warning') return 'Warnings';
    if (dispositionType === 'both') return 'Failures & Warnings';
    return '—';
  };

  const renderDispositionTable = (allItems) => {
    return (
      <div>
        <div style={{ marginBottom: 8, padding: '8px 0', color: '#666', fontSize: 13 }}>
          <Text type="secondary">💡 Drag a row to reorder</Text>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            const oldIndex = allItems.findIndex(item => item.id === active.id);
            const newIndex = allItems.findIndex(item => item.id === over.id);
            const movedItem = allItems[oldIndex];

            if (oldIndex !== newIndex) {
              // Get items of same status only
              const sameStatusItems = allItems.filter(item => item.status === movedItem.status);
              const oldStatusIndex = sameStatusItems.findIndex(item => item.id === active.id);
              const newStatusIndex = sameStatusItems.findIndex(item => item.id === over.id);

              const newItems = arrayMove(sameStatusItems, oldStatusIndex, newStatusIndex);
              const orderedIds = newItems.map(item => item.id);
              onReorder(movedItem.status, orderedIds);
            }
          }}
        >
          <SortableContext
            items={allItems.map(item => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#fafafa', borderBottom: '2px solid #f0f0f0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Disposition</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Comment Required</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Applies To</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Time-Bound</th>
                  <th style={{ width: 100, padding: '12px', textAlign: 'center', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allItems.map((item) => (
                  <SortableRow
                    key={item.id}
                    id={item.id}
                    item={item}
                    type={type}
                    onEdit={showEditModal}
                    onDelete={handleDelete}
                  >
                    <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                      <Tag color={item.status === 'in_progress' ? 'blue' : 'green'}>
                        {item.status === 'in_progress' ? 'In Progress' : 'Closed'}
                      </Tag>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                      <strong>{item.disposition_name}</strong>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                      {item.requires_comment ? 'Required' : 'Optional'}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                      {getAppliesTo(item.disposition_type)}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                      {item.is_time_bound ? 'Yes' : 'No'}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                      <Space>
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            showEditModal(item);
                          }}
                          title="Edit"
                        />
                        <Popconfirm
                          title="Delete this disposition?"
                          description="This action cannot be undone."
                          onConfirm={() => handleDelete(item.id)}
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                            title="Delete"
                          />
                        </Popconfirm>
                      </Space>
                    </td>
                  </SortableRow>
                ))}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>
    );
  };

  const renderList = (listItems, status = null) => {
    return (
      <div>
        <div style={{ marginBottom: 8, padding: '8px 0', color: '#666', fontSize: 13 }}>
          <Text type="secondary">💡 Drag a row to reorder</Text>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => handleDragEnd(event, status)}
        >
          <SortableContext
            items={listItems.map(item => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#fafafa', borderBottom: '2px solid #f0f0f0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>
                    {type === 'timeframes' ? 'Label' : 'Reason'}
                  </th>
                  {type === 'timeframes' && (
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Duration</th>
                  )}
                  <th style={{ width: 100, padding: '12px', textAlign: 'center', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listItems.map((item) => (
                  <SortableRow
                    key={item.id}
                    id={item.id}
                    item={item}
                    type={type}
                    onEdit={showEditModal}
                    onDelete={handleDelete}
                  >
                    <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                      <strong>{type === 'timeframes' ? item.label : item.reason_name}</strong>
                    </td>
                    {type === 'timeframes' && (
                      <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                        {item.duration_days !== null ? (
                          <Tag color="blue">{item.duration_days} days</Tag>
                        ) : (
                          <Tag color="purple">Indefinitely</Tag>
                        )}
                      </td>
                    )}
                    <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                      <Space>
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            showEditModal(item);
                          }}
                          title="Edit"
                        />
                        <Popconfirm
                          title={`Delete this ${type === 'timeframes' ? 'timeframe' : 'suppression reason'}?`}
                          description="This action cannot be undone."
                          onConfirm={() => handleDelete(item.id)}
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                            title="Delete"
                          />
                        </Popconfirm>
                      </Space>
                    </td>
                  </SortableRow>
                ))}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={showAddModal}
        >
          Add {getTitle().slice(0, -1)}
        </Button>
      </div>

      {type === 'dispositions' ? (
        <Card bodyStyle={{ padding: 16, overflow: 'auto' }}>
          {(inProgressItems.length > 0 || closedItems.length > 0) ? (
            renderDispositionTable([...inProgressItems, ...closedItems])
          ) : (
            <Alert message="No dispositions configured" type="info" showIcon />
          )}
        </Card>
      ) : (
        <Card bodyStyle={{ padding: 16, overflow: 'auto' }}>
          {items.length > 0 ? (
            renderList(items)
          ) : (
            <Alert message={`No ${getTitle().toLowerCase()} configured`} type="info" showIcon />
          )}
        </Card>
      )}

      <Modal
        title={getModalTitle()}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={editingItem ? 'Save' : 'Add'}
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            requires_comment: false,
            is_time_bound: false
          }}
        >
          {renderModalFields()}
        </Form>
      </Modal>
    </div>
  );
}

export default DispositionConfigPanel;
